import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Button, Input, List, Popconfirm, Tag, Typography } from 'antd'
import { ContentPath, hashContentPath } from 'common/src/ContentPath'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import {
    SectionCheckpointKind,
    SectionCheckpointMeta,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import {
    slimLeafNodeAtomFamily,
    slimLeafNodeInvalidationAtomFamily,
} from '@/atoms/slimLeafNode'
import { invalidate } from '@/atoms/types'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import {
    createSectionCheckpoint,
    listSectionCheckpoints,
    restoreSectionCheckpoint,
} from '@/api/cb/agentContent'
import styles from './SectionCheckpointsPanel.module.less'
import {
    formulaSheetEntriesAtom,
    formulaSheetEntriesInvalidatorAtom,
    lastSavedFormulaSheetEntriesAtom,
} from '@/atoms/formulaSheetEntries'

export interface SectionCheckpointsPanelProps {
    contentPath: ContentPath
}

const CHECKPOINT_KIND_TAG: Record<
    SectionCheckpointKind,
    { color: string; label: string }
> = {
    [SectionCheckpointKind.aiPre]: { color: 'orange', label: 'before AI' },
    [SectionCheckpointKind.aiPost]: { color: 'blue', label: 'after AI' },
    [SectionCheckpointKind.manual]: { color: 'green', label: 'manual' },
}

export const SectionCheckpointsPanel: React.FC<SectionCheckpointsPanelProps> = (
    props
): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const [checkpoints, setCheckpoints] = useState<SectionCheckpointMeta[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isMutating, setIsMutating] = useState<boolean>(false)
    const [newCheckpointLabel, setNewCheckpointLabel] = useState<string>('')
    const isFormulaSheetCheckpoint = props.contentPath.length === 0
    const invalidateFormulaSheetEntries = useSetRecoilState(
        formulaSheetEntriesInvalidatorAtom
    )
    const setLastSavedFormulaSheetEntries = useSetRecoilState(
        lastSavedFormulaSheetEntriesAtom
    )
    const resetFormulaSheetEntries = useResetRecoilState(
        formulaSheetEntriesAtom
    )

    const hashedContentPath = useMemo(
        (): string => hashContentPath(props.contentPath),
        [props.contentPath]
    )
    const resetSlimLeafNodeAtomFamily = useResetRecoilState(
        slimLeafNodeAtomFamily(hashedContentPath)
    )
    const setSlimLeafNodeInvalidationAtomFamily = useSetRecoilState(
        slimLeafNodeInvalidationAtomFamily(hashedContentPath)
    )
    const invalidateSlimLeafNode = useCallback((): void => {
        setSlimLeafNodeInvalidationAtomFamily((val) => invalidate(val))
        resetSlimLeafNodeAtomFamily()
    }, [resetSlimLeafNodeAtomFamily, setSlimLeafNodeInvalidationAtomFamily])

    const refreshCheckpoints = useCallback(async (): Promise<void> => {
        setIsLoading(true)
        try {
            const response = await listSectionCheckpoints({
                courseName,
                contentPath: props.contentPath,
            })
            if (response.data.success) {
                setCheckpoints(response.data.payload.checkpoints)
            } else {
                syncErrorMessage(
                    `Failed to load checkpoints: ${response.data.error}`
                )
            }
        } finally {
            setIsLoading(false)
        }
    }, [courseName, props.contentPath])

    useEffect(() => {
        void refreshCheckpoints()
    }, [refreshCheckpoints])

    const createCheckpoint = useCallback(async (): Promise<void> => {
        setIsMutating(true)
        try {
            const response = await createSectionCheckpoint({
                courseName,
                contentPath: props.contentPath,
                label: newCheckpointLabel.trim() || 'Manual checkpoint',
            })
            if (response.data.success) {
                syncSuccessMessage('Checkpoint created.')
                setNewCheckpointLabel('')
                await refreshCheckpoints()
            } else {
                syncErrorMessage(
                    `Failed to create checkpoint: ${response.data.error}`
                )
            }
        } finally {
            setIsMutating(false)
        }
    }, [courseName, newCheckpointLabel, props.contentPath, refreshCheckpoints])

    const restoreCheckpoint = useCallback(
        async (checkpoint: SectionCheckpointMeta): Promise<void> => {
            setIsMutating(true)
            try {
                const response = await restoreSectionCheckpoint({
                    checkpointId: checkpoint.id,
                })
                if (response.data.success) {
                    syncSuccessMessage(
                        `Restored checkpoint: "${checkpoint.label}"`
                    )
                    if (isFormulaSheetCheckpoint) {
                        setLastSavedFormulaSheetEntries([])
                        resetFormulaSheetEntries()
                        invalidateFormulaSheetEntries((value) => value + 1)
                    } else {
                        invalidateSlimLeafNode()
                    }
                    await refreshCheckpoints()
                } else {
                    syncErrorMessage(
                        `Failed to restore checkpoint: ${response.data.error}`
                    )
                }
            } finally {
                setIsMutating(false)
            }
        },
        [
            invalidateFormulaSheetEntries,
            invalidateSlimLeafNode,
            isFormulaSheetCheckpoint,
            refreshCheckpoints,
            resetFormulaSheetEntries,
            setLastSavedFormulaSheetEntries,
        ]
    )

    return (
        <div className={styles.sectionCheckpointsPanel}>
            <Typography.Title level={4}>
                {isFormulaSheetCheckpoint
                    ? 'Formula Sheet Checkpoints'
                    : 'Section Checkpoints'}
            </Typography.Title>
            <div className={styles.createRow}>
                <Input
                    placeholder={'Checkpoint label (optional)'}
                    value={newCheckpointLabel}
                    onChange={(event) =>
                        setNewCheckpointLabel(event.target.value)
                    }
                    onPressEnter={() => void createCheckpoint()}
                    disabled={isReadOnly}
                />
                <Button
                    type={'primary'}
                    loading={isMutating}
                    disabled={isReadOnly}
                    onClick={() => void createCheckpoint()}
                >
                    Create checkpoint
                </Button>
            </div>
            {isReadOnly && (
                <Typography.Text type={'secondary'}>
                    You have read-only access to this course, so you can't
                    create or restore checkpoints.
                </Typography.Text>
            )}
            <List
                loading={isLoading}
                dataSource={checkpoints}
                locale={{
                    emptyText: isFormulaSheetCheckpoint
                        ? 'No formula sheet checkpoints yet.'
                        : 'No checkpoints yet for this section.',
                }}
                renderItem={(checkpoint) => (
                    <List.Item
                        actions={[
                            <Popconfirm
                                key={'restore'}
                                title={`Restore "${checkpoint.label}"?`}
                                description={
                                    isFormulaSheetCheckpoint
                                        ? 'Replaces the full formula sheet with the checkpointed version. A safety checkpoint of the current state is created first.'
                                        : "Replaces this section's outline, summary, notecards and practice problems with the checkpointed versions. A safety checkpoint of the current state is created first."
                                }
                                onConfirm={() =>
                                    void restoreCheckpoint(checkpoint)
                                }
                                okText={'Restore'}
                            >
                                <Button
                                    danger
                                    loading={isMutating}
                                    disabled={isReadOnly}
                                >
                                    Restore
                                </Button>
                            </Popconfirm>,
                        ]}
                    >
                        <List.Item.Meta
                            title={
                                <span>
                                    <Tag
                                        color={
                                            CHECKPOINT_KIND_TAG[checkpoint.kind]
                                                .color
                                        }
                                    >
                                        {
                                            CHECKPOINT_KIND_TAG[checkpoint.kind]
                                                .label
                                        }
                                    </Tag>
                                    {checkpoint.label}
                                </span>
                            }
                            description={
                                checkpoint.counts.formulaEntries !== undefined
                                    ? `${checkpoint.counts.formulaEntries} formula entries · ${new Date(checkpoint.createdAt).toLocaleString()}`
                                    : `${checkpoint.counts.notecards} notecards · ${checkpoint.counts.practiceProblems} practice problems · ${new Date(checkpoint.createdAt).toLocaleString()}`
                            }
                        />
                    </List.Item>
                )}
            />
        </div>
    )
}
